/**
 * Microsoft Clarity Loader — RGPD Compliant
 *
 * Loads Clarity ONLY after explicit analytics cookie consent.
 * Do not call before CookieConsent grants analytics.
 */

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const CLARITY_PROJECT_ID =
  import.meta.env.VITE_CLARITY_PROJECT_ID || "y0pqqeu6gs";

let clarityLoaded = false;
let isLoadingClarity = false;

/**
 * Loads Microsoft Clarity after user consent (project id: y0pqqeu6gs).
 */
export const loadClarity = (): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      resolve();
      return;
    }

    if (clarityLoaded || typeof window.clarity === "function") {
      clarityLoaded = true;
      resolve();
      return;
    }

    if (isLoadingClarity) {
      const checkInterval = setInterval(() => {
        if (clarityLoaded) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
      return;
    }

    isLoadingClarity = true;

    // Official Clarity bootstrap (same as snippet from Clarity dashboard)
    window.clarity =
      window.clarity ||
      function (...args: unknown[]) {
        (window.clarity!.q = window.clarity!.q || []).push(args);
      };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;

    script.onload = () => {
      clarityLoaded = true;
      isLoadingClarity = false;
      if (import.meta.env.DEV) {
        console.log("[Clarity] ✅ Loaded", CLARITY_PROJECT_ID);
      }
      resolve();
    };

    script.onerror = () => {
      isLoadingClarity = false;
      if (import.meta.env.DEV) {
        console.error("[Clarity] ❌ Failed to load script");
      }
      // Non-blocking: analytics failure must not break the app
      resolve();
    };

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });
};

export const isClarityLoaded = (): boolean => {
  return clarityLoaded && typeof window !== "undefined" && typeof window.clarity === "function";
};
